// Validation and enrichment of extracted segments. Nothing reaches a trip unless it
// passes; anything that fails goes to Needs attention with the problems listed. Only what
// we need to place a booking is required: a flight without its code or number is saved
// (with a warning) so it can be filled in later, rather than held back.

import type { ExtractedSegment } from './extracted.ts';
import { airportInfo } from '../airports.ts';
import { isValidDate, isValidTime, isValidTimeZone, toInstant } from '../time.ts';

export interface ValidationOutcome {
  ok: ExtractedSegment[];
  /** Segments that failed, with the reasons (shown in the fix form). */
  failed: Array<{ segment: ExtractedSegment; problems: string[] }>;
  /** Non-blocking notes (e.g. unknown airport code). */
  warnings: string[];
}

const IATA = /^[A-Z]{3}$/;

/** Fill in time zones and cities we can derive, and tidy fields. Pure. */
export function enrich(seg: ExtractedSegment): ExtractedSegment {
  const s: ExtractedSegment = structuredClone(seg);
  if (s.type === 'flight') {
    for (const key of ['from', 'to'] as const) {
      const p = s[key];
      if (p?.code) {
        p.code = p.code.toUpperCase();
        const info = airportInfo(p.code);
        if (info) {
          p.city ??= info.city;
          p.timeZone ??= info.timeZone;
        }
      }
    }
    if (s.from?.timeZone) s.start.timeZone ??= s.from.timeZone;
    if (s.end && s.to?.timeZone) s.end.timeZone ??= s.to.timeZone;
    // "b6924" -> "B6 924"; a bare "924" stays as it is.
    if (s.number) s.number = s.number.replace(/\s+/g, '').toUpperCase().replace(/^([A-Z][A-Z0-9]|[0-9][A-Z])(\d{1,4}[A-Z]?)$/, '$1 $2');
  }
  if (s.confirmation) s.confirmation = s.confirmation.replace(/\s+/g, '').toUpperCase();
  if (!s.title) s.title = defaultTitle(s);
  return s;
}

export function defaultTitle(s: ExtractedSegment): string {
  switch (s.type) {
    case 'flight':
      return `${s.from?.code ?? s.from?.city ?? '?'} → ${s.to?.code ?? s.to?.city ?? '?'}`;
    case 'train':
      return `${s.from?.name ?? '?'} → ${s.to?.name ?? '?'}`;
    case 'stay':
      return s.place?.name ?? 'Stay';
    case 'car':
      return s.provider ? `${s.provider} rental` : 'Car rental';
    case 'reservation':
      return s.place?.name ?? 'Reservation';
    case 'activity':
      return s.place?.name ?? 'Activity';
  }
}

function problemsFor(s: ExtractedSegment, warnings: string[]): string[] {
  const p: string[] = [];
  const needDate = (label: string, v: string | undefined) => {
    if (!isValidDate(v)) p.push(`Missing: ${label}`);
  };
  const needTime = (label: string, v: string | undefined) => {
    if (!isValidTime(v)) p.push(`Missing: ${label}`);
  };

  switch (s.type) {
    case 'flight': {
      if (!s.from?.code || !IATA.test(s.from.code)) p.push('Missing: departure airport');
      if (!s.to?.code || !IATA.test(s.to.code)) p.push('Missing: arrival airport');
      needDate('departure date', s.start.date);
      needTime('departure time', s.start.time);
      if (s.end) {
        needDate('arrival date', s.end.date);
        needTime('arrival time', s.end.time);
      }
      if (!s.confirmation) warnings.push(`No confirmation code found for ${s.number ?? defaultTitle(s)}`);
      for (const key of ['from', 'to'] as const) {
        const code = s[key]?.code;
        if (code && IATA.test(code) && !airportInfo(code) && !isValidTimeZone(s[key]?.timeZone)) {
          warnings.push(`Unknown airport ${code}: times shown without a time zone.`);
        }
      }
      if (p.length === 0 && s.end?.time && isValidTimeZone(s.start.timeZone) && isValidTimeZone(s.end.timeZone)) {
        const dur = toInstant(s.end).getTime() - toInstant(s.start).getTime();
        if (dur <= 0) p.push('Arrival is before departure');
        else if (dur > 30 * 3_600_000) p.push('Flight is longer than 30 hours');
      }
      break;
    }
    case 'stay':
      if (!s.place?.name) p.push('Missing: place');
      needDate('check-in date', s.start.date);
      needDate('check-out date', s.end?.date);
      if (isValidDate(s.start.date) && isValidDate(s.end?.date) && s.end!.date <= s.start.date) {
        p.push('Check-out is not after check-in');
      }
      break;
    case 'car':
      needDate('pick-up date', s.start.date);
      if (s.end) needDate('drop-off date', s.end.date);
      if (!s.place?.name && !s.place?.address && !s.provider) p.push('Missing: pick-up location');
      break;
    case 'train':
      needDate('departure date', s.start.date);
      needTime('departure time', s.start.time);
      if (!s.from?.name) p.push('Missing: departure station');
      if (!s.to?.name) p.push('Missing: arrival station');
      break;
    case 'reservation':
      if (!s.place?.name) p.push('Missing: place');
      needDate('date', s.start.date);
      needTime('time', s.start.time);
      break;
    case 'activity':
      if (!s.title && !s.place?.name) p.push('Missing: what it is');
      needDate('date', s.start.date);
      break;
  }

  // Sanity: bookings far in the past or future are usually extraction mistakes.
  if (isValidDate(s.start.date)) {
    const year = Number(s.start.date.slice(0, 4));
    const nowYear = new Date().getUTCFullYear();
    if (year < nowYear - 1 || year > nowYear + 2) p.push(`Unlikely year ${year}`);
  }
  return [...new Set(p)];
}

/** Same booking listed twice (models sometimes repeat a leg). */
function sameLeg(a: ExtractedSegment, b: ExtractedSegment): boolean {
  const key = (s: ExtractedSegment) =>
    JSON.stringify([s.type, s.start.date, s.start.time, s.from?.code ?? s.from?.name, s.to?.code ?? s.to?.name, s.place?.name, s.number, !!s.cancelled]);
  return key(a) === key(b);
}

export function validateSegments(segments: ExtractedSegment[]): ValidationOutcome {
  const out: ValidationOutcome = { ok: [], failed: [], warnings: [] };
  const seen: ExtractedSegment[] = [];
  for (const raw of segments) {
    const s = enrich(raw);
    if (seen.some((x) => sameLeg(x, s))) continue;
    seen.push(s);
    // A cancellation only needs enough to find the existing booking.
    if (s.cancelled && (s.confirmation || s.number)) {
      out.ok.push(s);
      continue;
    }
    const problems = problemsFor(s, out.warnings);
    if (problems.length) out.failed.push({ segment: s, problems });
    else out.ok.push(s);
  }
  out.warnings = [...new Set(out.warnings)];
  return out;
}
